import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Upload, FileText, Database, AlertCircle, CheckCircle, Clock, Trash2, Eye, PartyPopper, X, Calendar, DollarSign, FileArchive, Users, AlertTriangle, FileSpreadsheet, Sparkles, RotateCcw, Zap, Skull, Bomb, Flame, Radiation } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import useProgress from '../hooks/useProgress';
import ProgressBar from '../components/ProgressBar';
import LoanDetailView from '../components/LoanDetailView';
import SystemFilesList from '../components/SystemFilesList';
import FieldsManagement from '../components/FieldsManagement';
import DataTable from '../components/DataTable';
import FieldDeletionModal from '../components/FieldDeletionModal';
import { useFilter } from '../contexts/FilterContext';
import { apiFetch } from '../utils/apiUtils';

const PageContainer = styled.div`
  background: white;
  border-radius: 8px;
  margin: 1rem;
  min-height: 400px;
  padding-bottom: 2rem; /* Added bottom padding to prevent content cutoff when scrolling */
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  padding: 1.5rem 1.5rem 0 1.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
  padding: 0 1.5rem;
`;

const UploadSection = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 2.4rem; /* Increased from 2rem to 2.4rem (20% increase) */
  text-align: center;
  margin: 0 1.5rem 2rem 1.5rem;
  background: #fafafa;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  ${props => props.isDragOver && `
    border-color: #3b82f6;
    background: #eff6ff;
    border-style: solid;
  `}
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background: #2563eb;
  }
`;

const UploadText = styled.div`
  color: #6b7280;
  margin-bottom: 1rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const StatLabelComponent = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;


const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${props => props.status === 'success' && `
    background: #dcfce7;
    color: #166534;
  `}
  
  ${props => props.status === 'warning' && `
    background: #fef3c7;
    color: #92400e;
  `}
  
  ${props => props.status === 'error' && `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

const CompletionMessage = styled.div`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin: 2rem 1.5rem 2rem 1.5rem; /* Match page margins */
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: slideIn 0.5s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CompletionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CompletionTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CompletionIcon = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const CompletionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const CompletionStat = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
`;

const CompletionStatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const CompletionStatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
`;

// Delete Management Styled Components
const DeleteManagementCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin: 2rem 1.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const DeleteCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const DeleteCardTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
`;

const DeleteWarning = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
`;

const FilesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
`;

const TableHeader = styled.th`
  background: #f8fafc;
  color: #374151;
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.875rem;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9fafb;
  }
  &:hover {
    background: #f3f4f6;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;
`;

const DeleteButton = styled.button`
  background: #9ca3af;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DeleteAllButton = styled.button`
  background: linear-gradient(135deg, #d946ef 0%, #a855f7 50%, #ec4899 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #ec4899 0%, #dc2626 50%, #b91c1c 100%);
    transform: translateY(-1px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StartFreshSection = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const StartFreshTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #92400e;
  font-size: 1rem;
  font-weight: 600;
`;

const StartFreshMessage = styled.p`
  margin: 0 0 1rem 0;
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const StartFreshDisclaimer = styled.div`
  background: #ffffff;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 0.75rem;
  margin: 1rem 0;
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.4;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingMessage = styled.div`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const LoadingSubMessage = styled.div`
  font-size: 0.875rem;
  color: #9ca3af;
`;

const IconContainer = styled.div`
  position: relative;
  cursor: pointer;
  
  /* Sparkle animation keyframes */
  @keyframes sparkle1 {
    0%, 20% { opacity: 0; transform: scale(0.8); }
    10% { opacity: 1; transform: scale(1.2); }
    100% { opacity: 0; transform: scale(0.8); }
  }
  
  @keyframes sparkle2 {
    0%, 40% { opacity: 0; transform: scale(0.6); }
    30% { opacity: 1; transform: scale(1.4); }
    100% { opacity: 0; transform: scale(0.6); }
  }
  
  @keyframes sparkle3 {
    0%, 60% { opacity: 0; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.1); }
    100% { opacity: 0; transform: scale(0.9); }
  }
  
  @keyframes sparkleOriginal {
    0%, 80% { opacity: 1; transform: scale(1); }
    70% { opacity: 0.3; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes rotateRefresh {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(-360deg); }
  }
  
  /* Animate the original sparkle on hover */
  &:hover .original-sparkle,
  &:hover .original-sparkle svg {
    animation: sparkleOriginal 1.5s infinite !important;
  }
  
  /* Slowly rotate the refresh arrow counter-clockwise */
  &:hover .refresh-arrow,
  &:hover .refresh-arrow svg {
    animation: rotateRefresh 3s linear infinite !important;
    transform-origin: center center !important;
  }
  
  /* Additional sparkles on hover */
  &:hover .sparkle-1,
  &:hover .sparkle-2,
  &:hover .sparkle-3,
  &:hover .sparkle-1 svg,
  &:hover .sparkle-2 svg,
  &:hover .sparkle-3 svg {
    opacity: 1;
  }
  
  .sparkle-1,
  .sparkle-1 svg {
    position: absolute;
    top: -8px;
    left: -8px;
    opacity: 0;
  }
  
  &:hover .sparkle-1,
  &:hover .sparkle-1 svg {
    animation: sparkle1 1.5s infinite !important;
  }
  
  .sparkle-2,
  .sparkle-2 svg {
    position: absolute;
    bottom: -8px;
    right: -8px;
    opacity: 0;
  }
  
  &:hover .sparkle-2,
  &:hover .sparkle-2 svg {
    animation: sparkle2 1.5s infinite !important;
  }
  
  .sparkle-3,
  .sparkle-3 svg {
    position: absolute;
    top: 50%;
    left: -12px;
    opacity: 0;
  }
  
  &:hover .sparkle-3,
  &:hover .sparkle-3 svg {
    animation: sparkle3 1.5s infinite !important;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  font-style: italic;
`;

const InstructionsContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const InstructionsTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 600;
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.color || '#8b5cf6'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
`;

const StepDescription = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.4;
`;


function SystemFiles() {
  const [searchParams] = useSearchParams();
  const { loanId } = useParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab');
  
  let searchQuery = '';
  try {
    const filterContext = useFilter();
    searchQuery = filterContext.searchQuery;
  } catch (error) {
    console.error('🔍 SystemFiles - useFilter() error:', error);
  }
  
  const [uploading, setUploading] = useState(false);
  const [records, setRecords] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  
  
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalFields: 0,
    duplicates: 0,
    lastImport: null
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanDetail, setLoanDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Add progress tracking
  const { progress, status, isActive, resetProgress } = useProgress();
  
  // Completion state
  const [completionMessage, setCompletionMessage] = useState(null);
  const [lastUploadResults, setLastUploadResults] = useState(null);
  
  // Delete management state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    filename: null
  });
  
  // Reset progress on mount to clear stale state from other pages
  useEffect(() => {
    resetProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Debug logging
  useEffect(() => {
    // Progress state tracking
  }, [progress, status, isActive]);

  
  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US');
  };

  // Show completion message when processing completes
  useEffect(() => {
    if (status === 'Processing complete!' && progress === 100 && lastUploadResults) {
      setCompletionMessage({
        filename: lastUploadResults.filename,
        records: formatNumber(lastUploadResults.records_processed),
        successful: formatNumber(lastUploadResults.records_successful),
        failed: formatNumber(lastUploadResults.records_failed),
        time: `${lastUploadResults.processing_time_seconds}s`,
        rate: `${formatNumber(parseFloat(lastUploadResults.records_per_second))}/sec`
      });
      
      // Auto-hide after 8 seconds
      setTimeout(() => {
        setCompletionMessage(null);
        setLastUploadResults(null);
      }, 8000);
    }
  }, [status, progress, lastUploadResults]);

  // Fetch records and field stats on component mount
  useEffect(() => {
    fetchRecords();
    fetchFieldStats();
  }, []);

  // Handle loan detail route
  useEffect(() => {
    if (loanId) {
      // Load the specific loan detail
      loadLoanDetail(loanId);
    } else {
      // Clear loan detail when not on a specific loan route
      setLoanDetail(null);
    }
  }, [loanId]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/system-files/records/unified?limit=5000');
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (data.loans) {
        setRecords(data.loans);
        setStats({
          totalRecords: data.total,
          totalFields: data.loans.reduce((sum, loan) => sum + (loan.total_fields || 0), 0),
          duplicates: 0, // No duplicates in unified view
          lastImport: data.loans.length > 0 ? data.loans[0].last_updated : null
        });
      }
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldStats = async () => {
    try {
      const response = await apiFetch('/system-files/fields/stats');
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (data.fields) {
        setFields(data.fields);
      }
    } catch (error) {
      console.error('Error fetching field stats:', error);
      // Set empty array on error - no fallback needed since we have the API
      setFields([]);
    }
  };

  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    processFiles(files);
    // Reset file input
    event.target.value = '';
  };

  const processFiles = async (files) => {

    // Validate all files
    const invalidFiles = files.filter(file => 
      !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')
    );
    
    if (invalidFiles.length > 0) {
      alert(`Please upload only Excel files (.xlsx or .xls). Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setUploading(true);
    
    try {
      const results = [];
      
      // Process each file sequentially
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await apiFetch('/system-files/upload-excel', {
          method: 'POST',
          body: formData,
        });

        // apiFetch already returns parsed JSON
        const result = response;
        results.push({ filename: file.name, ...result });
      }
      
      // Show summary of results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        // Store the last successful result for completion message
        setLastUploadResults(successful[successful.length - 1]);
        fetchRecords(); // Refresh the records
        fetchUploadedFiles(); // Refresh the uploaded files list
      }
      
      if (failed.length > 0) {
        alert(`❌ Failed to process ${failed.length} files: ${failed.map(r => `${r.filename}: ${r.error || 'Unknown error'}`).join(', ')}`);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const loadLoanDetail = async (loanId) => {
    setDetailLoading(true);
    setLoanDetail(null);
    
    try {
      const response = await apiFetch(`/system-files/records/loan/${loanId}`);
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (data.success !== false) {
        setLoanDetail(data);
      } else {
        console.error('Error fetching loan details:', data);
        alert(`Error loading loan details: ${data.detail || 'Unknown error'}`);
        // Navigate back to list if loan not found
        navigate('/system-files');
      }
    } catch (error) {
      console.error('Error fetching loan details:', error);
      alert(`Error loading loan details: ${error.message}`);
      // Navigate back to list on error
      navigate('/system-files');
    } finally {
      setDetailLoading(false);
    }
  };

  const viewRecord = async (loanId) => {
    // Navigate to the loan detail route
    navigate(`/system-files/${loanId}`);
  };

  const closeLoanDetail = () => {
    // Navigate back to the system files list
    navigate('/system-files');
  };

  // Format field values for display
  const formatFieldValue = (fieldName, value, status) => {
    if (status === 'not_found') {
      return 'NOT FOUND';
    }
    
    if (!value) {
      return 'No data';
    }
    
    // Format currency fields
    if (fieldName.toLowerCase().includes('amount') || fieldName.toLowerCase().includes('balance')) {
      const numValue = parseFloat(value.replace(/[,$]/g, ''));
      if (!isNaN(numValue)) {
        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
    
    // Format date fields
    if (fieldName.toLowerCase().includes('date')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          });
        }
      } catch (e) {
        // If date parsing fails, return original value
      }
    }
    
    // Format boolean-like fields
    const booleanValues = ['yes', 'no', 'true', 'false', 'y', 'n'];
    if (booleanValues.includes(value.toLowerCase())) {
      const isTrue = ['yes', 'true', 'y'].includes(value.toLowerCase());
      return (
        <span style={{ 
          color: isTrue ? '#059669' : '#dc2626',
          fontWeight: '600'
        }}>
          {isTrue ? '✓ YES' : '✗ NO'}
        </span>
      );
    }
    
    return value;
  };

  const deleteRecord = async (loanId) => {
    if (!confirm(`Are you sure you want to delete record ${loanId}?`)) {
      return;
    }

    try {
      const response = await apiFetch(`/system-files/records/${loanId}`, {
        method: 'DELETE',
      });

      // apiFetch already returns parsed JSON
      const result = response;
      
      if (result.success) {
        alert(`Record ${loanId} deleted successfully`);
        fetchRecords(); // Refresh the records
      } else {
        alert(`Error: ${result.error || 'Failed to delete record'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting record. Please try again.');
    }
  };

  const deleteAllRecords = async () => {
    if (!confirm('Are you sure you want to delete ALL records? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiFetch('/system-files/records/all', {
        method: 'DELETE',
      });

      // apiFetch already returns parsed JSON
      const result = response;
      
      if (result.success) {
        alert(`Successfully deleted ${result.records_deleted} records and ${result.fields_deleted} field results`);
        fetchRecords(); // Refresh the records
      } else {
        alert(`Error: ${result.error || 'Failed to delete all records'}`);
      }
    } catch (error) {
      console.error('Delete all error:', error);
      alert('Failed to delete all records');
    }
  };

  // Fetch uploaded files list
  const fetchUploadedFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await apiFetch('/system-files/uploaded-files');
      // apiFetch already returns parsed JSON
      const data = response;
      setUploadedFiles(data.files || []);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Delete specific file data
  const handleDeleteFile = useCallback(async (filename) => {
    setDeleteModalState({
      isOpen: true,
      filename: filename
    });
  }, []);

  // Handle confirmed file deletion
  const handleConfirmedDelete = async () => {
    if (!deleteModalState.filename) return;
    
    setDeletingFile(deleteModalState.filename);
    try {
      await fetchUploadedFiles(); // Refresh the list
    } catch (error) {
      console.error('Error refreshing files:', error);
    } finally {
      setDeletingFile(null);
      setDeleteModalState({ isOpen: false, filename: null });
    }
  };

  // Delete all records with strong confirmation
  const handleDeleteAllFiles = async () => {
    if (!showDeleteAllConfirm) {
      setShowDeleteAllConfirm(true);
      return;
    }

    if (!confirm('⚠️ FINAL WARNING: This will delete ALL uploaded data from the system. This action is IRREVERSIBLE. Are you absolutely sure?')) {
      setShowDeleteAllConfirm(false);
      return;
    }

    setDeletingFile('ALL');
    try {
      const response = await apiFetch('/system-files/delete-all', {
        method: 'DELETE',
      });

      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        await fetchUploadedFiles(); // Refresh the list
        setShowDeleteAllConfirm(false);
        alert('Successfully deleted all data from the system');
      } else {
        alert(`Failed to delete all data: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting all data:', error);
      alert('Error deleting data. Please try again.');
    } finally {
      setDeletingFile(null);
      setShowDeleteAllConfirm(false);
    }
  };

  // Load uploaded files on component mount
  useEffect(() => {
    fetchUploadedFiles();
  }, []);

  // Show different content based on active tab
  if (activeTab === 'fields') {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view') || 'all';
    
    return <FieldsManagement view={view} />;
  }
  
  if (activeTab === 'add-data') {
    return (
      <>
      <PageContainer>
        <Title>Add Data</Title>
        <Subtitle>Upload and manage system records from Excel files</Subtitle>
        
        
        
        {/* Upload Section - Hide during processing */}
        {!isActive && !completionMessage && (
        <UploadSection 
          isDragOver={isDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('excel-upload').click()}
        >
          <UploadText>
            {isDragOver 
              ? 'Drop Excel files here...' 
              : 'Upload one or more Excel files containing system data (Portfolio Scrub Reports, Master Files, etc.)'
            }
          </UploadText>
          <UploadInput
            type="file"
            id="excel-upload"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={uploading}
            multiple
          />
          <UploadLabel htmlFor="excel-upload" style={{ opacity: uploading ? 0.5 : 1 }}>
            {uploading ? <LoadingSpinner /> : <Upload size={16} />}
            {uploading ? 'Processing...' : 'Upload Excel Files'}
          </UploadLabel>
        </UploadSection>
      )}

      {/* Progress Bar - Only show when processing and not completed */}
      {isActive && !completionMessage && (
        <div style={{ margin: '2rem 1.5rem' }}>
          <ProgressBar 
            progress={progress} 
            status={status}
            style={{ marginBottom: '2rem' }}
          />
        </div>
      )}
      
      {/* Completion Message */}
      {completionMessage && (
        <CompletionMessage>
          <CompletionHeader>
            <CompletionIcon>
              <PartyPopper size={24} />
            </CompletionIcon>
            <CompletionTitle>Processing Complete!</CompletionTitle>
          </CompletionHeader>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>{completionMessage.filename}</strong> has been successfully processed!
          </div>
          
          <CompletionDetails>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.records}</CompletionStatValue>
              <CompletionStatLabel>Records Processed</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.successful}</CompletionStatValue>
              <CompletionStatLabel>Successful</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.time}</CompletionStatValue>
              <CompletionStatLabel>Processing Time</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.rate}</CompletionStatValue>
              <CompletionStatLabel>Processing Rate</CompletionStatLabel>
            </CompletionStat>
          </CompletionDetails>
        </CompletionMessage>
      )}

      {/* Delete Management Section */}
      <DeleteManagementCard>
          <DeleteCardHeader>
            <Sparkles size={24} color="#059669" />
            <DeleteCardTitle>Cleanup and Review</DeleteCardTitle>
          </DeleteCardHeader>
        
        <DeleteWarning>
          <AlertTriangle size={20} />
          <span>
            <strong>Warning:</strong> Deleting data is permanent and cannot be undone. 
            Use this feature carefully to remove outdated or incorrect uploads.
          </span>
        </DeleteWarning>

        {loadingFiles ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
            Loading uploaded files...
          </div>
        ) : (uploadedFiles || []).length === 0 ? (
          <EmptyState>
            No files have been uploaded yet.
          </EmptyState>
        ) : (
          <>
            <DataTable
              columns={[
                {
                  header: 'Filename',
                  field: 'source_file',
                  type: 'text',
                  width: 'minmax(200px, 1fr)',
                  render: (row) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileSpreadsheet size={16} color="#6b7280" />
                      <span style={{ fontWeight: 500 }}>{row.source_file}</span>
                    </div>
                  )
                },
                {
                  header: 'Field Count',
                  field: 'field_count',
                  type: 'number',
                  width: 'minmax(100px, 140px)',
                  render: (row) => (
                    <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                      {row.field_count?.toLocaleString() || '0'}
                    </span>
                  )
                },
                {
                  header: 'Loan Count',
                  field: 'loan_count',
                  type: 'number', 
                  width: 'minmax(100px, 140px)',
                  render: (row) => (
                    <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                      {row.loan_count?.toLocaleString() || '0'}
                    </span>
                  )
                },
                {
                  header: 'Date Uploaded',
                  field: 'uploaded_date',
                  type: 'date',
                  width: 'minmax(120px, 160px)',
                  render: (row) => (
                    <span style={{ color: '#6b7280' }}>
                      {row.uploaded_date ? new Date(row.uploaded_date).toLocaleDateString() : 'Unknown'}
                    </span>
                  )
                },
                {
                  header: 'Actions',
                  field: 'actions',
                  type: 'action',
                  width: 'minmax(100px, 120px)',
                  align: 'center',
                  render: (row) => (
                    <DeleteButton
                      onClick={() => handleDeleteFile(row.source_file)}
                      disabled={deletingFile === row.source_file}
                    >
                      {deletingFile === row.source_file ? 'Deleting...' : 'Delete'}
                    </DeleteButton>
                  )
                }
              ]}
              data={uploadedFiles || []}
              keyField="source_file"
              density="regular"
            />

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Total: {(uploadedFiles || []).reduce((sum, file) => sum + (file.field_count || 0), 0).toLocaleString()} fields across {(uploadedFiles || []).length} files
              </div>
            </div>

            <StartFreshSection>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <IconContainer>
                  <RotateCcw size={32} color="#f59e0b" className="refresh-arrow" />
                  <Sparkles 
                    size={18} 
                    color="#059669" 
                    className="original-sparkle"
                    style={{ position: 'absolute', top: -6, right: -6 }} 
                  />
                  <Sparkles size={14} color="#059669" className="sparkle-1" />
                  <Sparkles size={16} color="#059669" className="sparkle-2" />
                  <Sparkles size={12} color="#059669" className="sparkle-3" />
                </IconContainer>
                <StartFreshTitle style={{ margin: 0 }}>Need to Start Fresh?</StartFreshTitle>
              </div>
              <StartFreshMessage>
                Sometimes it's better to just start from scratch. This will clear all your uploaded system data and let you begin with a clean slate.
              </StartFreshMessage>
              
              <StartFreshDisclaimer>
                <strong>Important:</strong> This will ONLY delete system data (Excel uploads). This WILL NOT affect any matching results or scanned document data.
              </StartFreshDisclaimer>
              
              <DeleteAllButton
                onClick={handleDeleteAllFiles}
                disabled={deletingFile === 'ALL'}
                style={{ 
                  background: showDeleteAllConfirm ? '#dc2626' : '#9ca3af',
                  transform: showDeleteAllConfirm ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                <span style={{ fontSize: '24px', lineHeight: 1 }}>☢️</span>
                {deletingFile === 'ALL' 
                  ? 'Deleting All System Data...' 
                  : showDeleteAllConfirm 
                    ? 'Click Again to Confirm' 
                    : 'Delete All Saved System Data'
                }
              </DeleteAllButton>
            </StartFreshSection>
          </>
        )}
      </DeleteManagementCard>

      </PageContainer>
      
      {/* Field Deletion Modal - Outside PageContainer for Add Data tab */}
      <FieldDeletionModal
        isOpen={deleteModalState.isOpen}
        onClose={() => {
          setDeleteModalState({ isOpen: false, filename: null });
        }}
        filename={deleteModalState.filename}
        onConfirm={handleConfirmedDelete}
      />
      </>
    );
  }

  // Default view - show records and stats
  return (
    <>
    <PageContainer>
      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingMessage>Collecting System Data</LoadingMessage>
          <LoadingSubMessage>Please wait while we gather your loan records and field information...</LoadingSubMessage>
        </LoadingContainer>
      ) : !loanDetail && (
        <>
            <Title>System Files</Title>
            <Subtitle>Upload and manage system records from Excel files</Subtitle>
            
        </>
      )}
      
      {/* Progress Bar - Only show when processing and not completed */}
      {isActive && !completionMessage && (
        <div style={{ margin: '2rem 1.5rem' }}>
          <ProgressBar 
            progress={progress} 
            status={status}
            style={{ marginBottom: '2rem' }}
          />
        </div>
      )}
      
      {/* Completion Message */}
      {completionMessage && (
        <CompletionMessage>
          <CompletionHeader>
            <CompletionIcon>
              <PartyPopper size={24} />
            </CompletionIcon>
            <CompletionTitle>Processing Complete!</CompletionTitle>
          </CompletionHeader>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>{completionMessage.filename}</strong> has been successfully processed!
          </div>
          
          <CompletionDetails>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.records}</CompletionStatValue>
              <CompletionStatLabel>Records Processed</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.successful}</CompletionStatValue>
              <CompletionStatLabel>Successful</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.time}</CompletionStatValue>
              <CompletionStatLabel>Processing Time</CompletionStatLabel>
            </CompletionStat>
            <CompletionStat>
              <CompletionStatValue>{completionMessage.rate}</CompletionStatValue>
              <CompletionStatLabel>Processing Rate</CompletionStatLabel>
            </CompletionStat>
          </CompletionDetails>
        </CompletionMessage>
      )}


      {/* Loan Detail View */}
      {loanId && (
        detailLoading ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '3rem',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <LoadingSpinner size="large" style={{ marginRight: '1rem' }} />
            <div>
              <Database size={20} style={{ marginRight: '0.5rem', display: 'inline-block' }} />
              Loading loan details...
            </div>
          </div>
        ) : loanDetail ? (
          <LoanDetailView 
            loanDetail={loanDetail} 
            onClose={closeLoanDetail}
            splitView={true}
          />
        ) : null
      )}

      {/* Records Table - Only show when not viewing a specific loan */}
      {!loanId && (
        <SystemFilesList
          key={`system-files-list-${searchQuery}`}
          records={records}
          fields={fields}
          onView={viewRecord}
          initialViewMode="loans"
          searchTerm={searchQuery}
        />
      )}

    </PageContainer>
    
    {/* Field Deletion Modal - Outside PageContainer */}
    <FieldDeletionModal
      isOpen={deleteModalState.isOpen}
      onClose={() => {
        setDeleteModalState({ isOpen: false, filename: null });
      }}
      filename={deleteModalState.filename}
      onConfirm={handleConfirmedDelete}
    />
    </>
  );
}

export default SystemFiles;
